import React, { useEffect, useState } from 'react';
import moment from 'moment-timezone';
import styled, { keyframes } from 'styled-components';

import { Alert, AlertTitle } from '@material-ui/lab';
import { Box, Button, Chip, Divider, Typography, useTheme } from '@material-ui/core';

import requestValidator from '../../domain/validators/request.validator';

import billingsService from '../../domain/services/financial/billing.service';
import { Link, useHistory } from 'react-router-dom';
import LoadingSpin from '../LoadingSpin';
import requestAdvanceService from '../../domain/services/request/advance.service';
import expressDeliveryService from '../../domain/services/request/express.delivery.service';
import datetimeProvider from '../../providers/datetime.provider';
import { Info, Schedule, FlashOn } from '@material-ui/icons';

import { requestServices, servicesByTypeKey } from '../../domain/constant/request.services.constant';
import { expressDeliveryConstants } from '../../domain/constant/express.delivery.constant';
import { appConstants } from '../../domain/constant/app.constant';
import SimpleModal from '../shared/SimpleModal';
import solicitationService from '../../domain/services/solicitation/solicitation.service';
import FinancialPendingAfterRegistration from "./financialPendingAfterRegistration";

const {
    BRT_TIMEZONE,
    BADGE_COLORS,
} = expressDeliveryConstants;

const totalPulse = keyframes`
    from { opacity: 0.4; transform: scale(0.95); }
    to { opacity: 1; transform: scale(1); }
`;

const AnimatedTotal = styled.span`
    animation: ${totalPulse} 400ms ease-out;
`;

export default function RequestConclusion({ request, errorFinished, setMultipleValues, hideAdvanceButton = false, replaceHistoryOnRedirect = false }) {
    const history = useHistory();
    const muiTheme = useTheme();
    const [services, setServices] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(false);
    const [advanceOptions, setAdvanceOptions] = useState([]);
    const [selectedOptionId, setSelectedOptionId] = useState('standard');

    const [createdRequest, setCreatedRequest] = useState({});

    useEffect(() => {
        if (request.status == 'NEW' || !request.id) return;

        loadServicesReceipt();

        solicitationService.find(request.id)
            .then(({ data }) => setCreatedRequest(data));

        if (requestValidator.canAdvanceRequest(request) && !hideAdvanceButton) {
            requestAdvanceService.findAvailableByMainId(request.id)
                .then(options => setAdvanceOptions(options || []));
        }

    }, [request.status]);

    const loadServicesReceipt = async () =>
        billingsService.findByRequestId(request.id).then(setServices);

    const redirectToRequest = () => {
        const url = `/solicitations/${request.id}`;

        if (replaceHistoryOnRedirect) {
            history.replace(url);
            return;
        }

        history.push(url);
    };

    const saveAdvance = (advanceData) => {
        setError(false);
        setLoading(true);

        requestAdvanceService
            .advance(request.id, advanceData.id, !!advanceData.canSkipValidation)
            .then(({ allServicesDueDate }) => {
                if (setMultipleValues) {
                    setMultipleValues({
                        allServicesDueDate: allServicesDueDate,
                        isDueDateAdvanced: true,
                        advancedDueDate: true
                    });
                }

                redirectToRequest();
            })
            .catch((err) => {
                const message = err?.response?.data?.message || err?.message;
                setError(message || true);
            })
            .finally(() => setLoading(false));
    };

    const handleOptionSelect = (optionId) => {
        if (loading) return;

        setError(false);
        setSelectedOptionId(optionId);
    };

    const handleConcluir = () => {
        if (loading) return;

        if (selectedOptionId === 'standard') {
            setLoading(true);
            redirectToRequest();
            return;
        }

        const advanceData = advanceOptions.find(option => option.id === selectedOptionId);
        if (!advanceData) return;

        saveAdvance(advanceData);
    };

    const getAdvanceType = (option) => {
        if (option.hoursToDueDate < 24) return 'express';
        if (option.hoursToDueDate <= 24) return 'urgente';
        return null;
    };

    const formatCurrency = (value) =>
        new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(parseFloat(value));

    const getSelectedAdvancePrice = () => {
        if (selectedOptionId === 'standard') return 0;
        const option = advanceOptions.find(o => o.id === selectedOptionId);
        return option?.billing?.amount || 0;
    };

    const getSelectedAdvanceOption = () => {
        if (selectedOptionId === 'standard') return null;
        return advanceOptions.find(option => option.id === selectedOptionId) || null;
    };

    const getServicesTotal = () => {
        const servicesSum = services.reduce((sum, service) => sum + parseFloat(service.amount || 0), 0);
        return servicesSum + getSelectedAdvancePrice();
    };

    const supportButton = () => (
        <Link
            to={{ pathname: appConstants.whatsappSupportUrl }}
            target="_blank"
            style={{ fontWeight: 500, textDecoration: 'none', color: 'blue' }}
        >
            <span>Suporte da Facilita Jurídico</span>
        </Link>
    );

    const getErrorContent = () => (
        <div style={{ textAlign: 'justify', width: '100%' }}>
            <Alert severity="warning">
                <AlertTitle>Não foi possível concluir o cadastro</AlertTitle>
                <p>{errorFinished}</p>
                <strong>Contate o suporte através do link</strong>:{supportButton()}
            </Alert>
        </div>
    );

    const getRequestIdButton = () => (
        <Link to={`/solicitations/${request.id}`} style={{ color: muiTheme.palette.primary.main }}>
            #{request.id}
        </Link>
    );

    const renderDeliveryCard = (id, title, subtitle, badge, price, disabled, errorMsg) => {
        const isSelected = selectedOptionId === id;
        const primaryColor = muiTheme.palette.primary.main;
        return (
            <Box
                key={id}
                onClick={() => !disabled && handleOptionSelect(id)}
                style={{
                    border: `2px solid ${isSelected ? primaryColor : '#e0e0e0'}`,
                    borderRadius: 12,
                    padding: '16px 20px',
                    marginBottom: 10,
                    cursor: disabled ? 'not-allowed' : 'pointer',
                    opacity: disabled ? 0.5 : 1,
                    backgroundColor: isSelected ? `${primaryColor}0a` : '#fff',
                    transition: 'all .2s ease'
                }}
            >
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                    <div style={{
                        width: 20, height: 20, borderRadius: '50%', flexShrink: 0,
                        border: `2px solid ${isSelected ? primaryColor : '#bdbdbd'}`,
                        display: 'flex', alignItems: 'center', justifyContent: 'center'
                    }}>
                        {isSelected && <div style={{ width: 10, height: 10, borderRadius: '50%', backgroundColor: primaryColor }} />}
                    </div>
                    <span style={{ fontWeight: 600, fontSize: 15 }}>{title}</span>
                    {badge && (
                        <Chip
                            icon={<FlashOn style={{ fontSize: 14, color: badge.color }} />}
                            label={badge.label}
                            size="small"
                            style={{
                                backgroundColor: badge.bg,
                                color: badge.color,
                                fontWeight: 600,
                                fontSize: 11,
                                height: 22
                            }}
                        />
                    )}
                    <span style={{ fontWeight: 600, fontSize: 14, color: price ? primaryColor : '#757575', marginLeft: 'auto' }}>
                        {price ? `+ ${formatCurrency(price)}` : 'sem acréscimo'}
                    </span>
                </div>
                <div style={{ marginLeft: 30, marginTop: 4, fontSize: 13, color: isSelected ? primaryColor : '#757575' }}>
                    {subtitle}
                </div>
                {errorMsg && (
                    <div style={{ marginLeft: 30, marginTop: 4, fontSize: 12, color: '#e53935' }}>
                        {errorMsg}
                    </div>
                )}
            </Box>
        );
    };

    const getSuccessContent = () => {
        if (
            [servicesByTypeKey.hearing.id].includes(request.serviceTypeId) &&
            datetimeProvider.getBusinessDatesCount(request.hearingDateTime) < 5
        ) {
            return getHearingAdvanceContent();
        }

        const dueDate = createdRequest.allServicesDueDate || request.allServicesDueDate;
        if (!dueDate) return <LoadingSpin />;

        const standardDueDate = moment.utc(dueDate.slice(0, 16), 'YYYY-MM-DD HH:mm')
            .tz('America/Sao_Paulo');

        const packageHours = services.find(s => s.requestHoursToDueDate)?.requestHoursToDueDate;
        const packageBusinessDays = packageHours ? Math.round(packageHours / 24) : null;

        const showAdvanceCards = requestValidator.canAdvanceRequest(request) && !hideAdvanceButton && advanceOptions.length > 0;

        return (
            <div style={{ display: 'flex', flexDirection: 'column' }}>
                <span className="!font-semibold" style={{ fontSize: 20, marginBottom: 20 }}>
                    Solicitação {getRequestIdButton()} cadastrada com sucesso.
                </span>

                {showAdvanceCards ? (
                    <>
                        <Typography style={{ fontSize: 15, fontWeight: 500, marginBottom: 12, color: '#424242', display: 'flex', alignItems: 'center', gap: 6 }}>
                            <Schedule style={{ fontSize: 18 }} />
                            Em quantos dias você quer receber a solicitação?
                        </Typography>

                        {renderDeliveryCard(
                            'standard',
                            'Prazo padrão',
                            packageBusinessDays
                                ? `${packageBusinessDays} ${packageBusinessDays === 1 ? 'dia útil' : 'dias úteis'} · ${standardDueDate.format('DD [de] MMMM')}`
                                : `Entrega em ${standardDueDate.format('DD [de] MMMM [de] YYYY')}`,
                            null, 0, loading
                        )}

                        {advanceOptions.map(option => {
                                const type = getAdvanceType(option);
                                const badge = type === 'express'
                                    ? { label: 'Express', ...BADGE_COLORS.express }
                                    : type === 'urgente'
                                        ? { label: 'Urgente', ...BADGE_COLORS.urgent }
                                        : null;
                                const dueDate = moment.tz(option.newDueDate, BRT_TIMEZONE).format('DD [de] MMMM');
                                const subtitle = type === 'express'
                                    ? expressDeliveryService.describeExpressDelivery(option.newDueDate) || dueDate
                                    : `Em até ${option.hoursToDueDate}h · ${dueDate}`;
                                const disabled = loading || requestAdvanceService.isAdvanceOptionUnavailable(option);

                                const errorMsg = requestAdvanceService.getAdvanceUnavailableReason(option);

                                return renderDeliveryCard(
                                    option.id, option.name || `Entrega em ${Math.ceil(option.hoursToDueDate / 24)} dia(s)`,
                                    subtitle, badge, option.billing?.amount,
                                    disabled, errorMsg
                                );
                            })}
                    </>
                ) : (
                    <div className="flex flex-wrap items-center text-base gap-2 pb-1 mt-2">
                        <Typography style={{ fontSize: 14, color: '#757575' }}>
                            Previsão de entrega: <strong>{standardDueDate.format('DD [de] MMMM [de] YYYY')}</strong>
                        </Typography>
                    </div>
                )}
            </div>
        );
    };

    const getHearingAdvanceContent = () => {
        return (
            <>
                <div style={{ display: 'flex', flexDirection: 'column' }}>
                    <Typography style={{ fontSize: 20, fontWeight: 500, marginBottom: 30 }}>
                        Solicitação cadastrada com sucesso!
                    </Typography>
                    <Typography style={{ fontSize: 15, marginBottom: 10 }}>
                        Número Identificador da Solicitação:{' '}
                        <strong style={{ marginLeft: '2px' }}> {request.id} </strong>
                    </Typography>
                    <Typography style={{ fontSize: 15, marginBottom: 5 }}>
                        Entrega em:{' '}
                        <strong>
                            {' '}
                            {moment(request.hearingDateTime)
                                .add(1, 'days')
                                .format('DD [de] MMMM [de] YYYY [às] HH:mm')}
                        </strong>
                    </Typography>
                    <div
                        style={{
                            display: 'flex',
                            width: '100%',
                            justifyContent: 'center',
                            marginTop: 10
                        }}
                    >
                        <span
                            style={{
                                borderRadius: '5px',
                                border: `1px solid ${muiTheme.palette.primary.main}`,
                                padding: '15px',
                                color: muiTheme.palette.primary.main,
                                cursor: 'default',
                                fontWeight: 'bold',
                                marginLeft: 5
                            }}
                        >
                            Prazo de entrega antecipado!
                        </span>
                    </div>
                </div>
            </>
        );
    };

    if (errorFinished) return getErrorContent();

    const selectedAdvanceOption = getSelectedAdvanceOption();
    const showSelectedAdvanceSummary = !!selectedAdvanceOption;

    const getFaciliterComuteChargeInfo = () => {

        const hasHearingInPersonService = services.some(service => service.serviceKey == requestServices.structure.hearingInPerson.key);
        if (!hasHearingInPersonService)
            return;

        return (
            <div className='p-3 bg-blue-200 rounded mt-5'>
                <p>
                    <Info fontSize='small' className='mr-2 mt-[-2px] text-blue-600' />
                    Informamos que poderá haver uma cobrança de deslocamento do audiencista até o local da audiência. Fique tranquilo, você será avisado caso isso seja necessário.
                </p>
            </div>
        )
    }

    if (!createdRequest.id)
        return <></>;

    const dueDateReady = createdRequest.isFinancialPending || createdRequest.allServicesDueDate || request.allServicesDueDate;
    if (!dueDateReady)
        return <LoadingSpin />;

    return (
        <SimpleModal
            open={true}
            onClose={() => {
                if (loading) return;
                redirectToRequest();
            }}
            bodyStyle={{ padding: createdRequest.isFinancialPending ? undefined : 0 }}
            maxWidth="sm"
            showClose={false}
            disableBackdropClick={loading}
            disableEscapeKeyDown={loading}
        >
            {createdRequest.isFinancialPending ? (
                <FinancialPendingAfterRegistration request={request} />
            ) : (
                <div style={{ textAlign: 'justify', fontSize: '14px', padding: '24px 20px' }}>
                    {getSuccessContent()}

                    {services.length > 0 && (
                        <div style={{ marginTop: 20, paddingTop: 16 }}>
                            {services.map((service, index) => (
                                <div key={index} className="flex justify-between py-1" style={{ fontSize: 13, color: '#424242' }}>
                                    <span>{service.serviceName}</span>
                                    <span>{formatCurrency(service.amount)}</span>
                                </div>
                            ))}
                            <div
                                style={{
                                    maxHeight: showSelectedAdvanceSummary ? 48 : 0,
                                    opacity: showSelectedAdvanceSummary ? 1 : 0,
                                    transform: showSelectedAdvanceSummary ? 'translateY(0)' : 'translateY(-10px)',
                                    overflow: 'hidden',
                                    transition: 'max-height 420ms cubic-bezier(0.22, 1, 0.36, 1), opacity 320ms ease, transform 420ms cubic-bezier(0.22, 1, 0.36, 1)'
                                }}
                            >
                                {selectedAdvanceOption && (
                                    <div className="flex justify-between py-1" style={{ fontSize: 13, color: muiTheme.palette.primary.main }}>
                                        <span>{selectedAdvanceOption.name || 'Antecipação'}</span>
                                        <span>+ {formatCurrency(selectedAdvanceOption.billing?.amount)}</span>
                                    </div>
                                )}
                            </div>
                            <Divider style={{ margin: '8px 0' }} />
                            <div className="flex justify-between py-1" style={{ fontSize: 15, fontWeight: 700 }}>
                                <span>Total</span>
                                <AnimatedTotal key={getServicesTotal()}>
                                    {formatCurrency(getServicesTotal())}
                                </AnimatedTotal>
                            </div>
                        </div>
                    )}

                    {getFaciliterComuteChargeInfo()}

                    <div className="mt-6">
                        <Button
                            variant="contained"
                            color="primary"
                            fullWidth
                            className="!py-3 !font-semibold !text-base"
                            onClick={handleConcluir}
                            disabled={loading}
                            style={{ borderRadius: 8 }}
                        >
                            {loading ? <LoadingSpin color="#fff" /> : 'Concluir'}
                        </Button>
                    </div>

                    <div style={{ marginTop: 16, fontSize: 11, color: '#9e9e9e', textAlign: 'center' }}>
                        *A Facilita Jurídico não se responsabiliza pela tempestividade da solicitação.
                    </div>

                    {error && (
                        <div className="mt-4">
                            <Alert severity="error">
                                <AlertTitle>
                                    {typeof error === 'string' ? error : 'Ocorreu um problema ao confirmar a antecipação, por favor tente novamente'}
                                </AlertTitle>
                            </Alert>
                        </div>
                    )}
                </div>
            )}
        </SimpleModal>
    );
}
